import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Star, ArrowRight } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import SmartImage from "../components/SmartImage";
import { api } from "../lib/config";
import useSEO from "../hooks/useSEO";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.8, ease: "easeOut" },
};

const HomePage = () => {
  const [hero, setHero] = useState(null);
  const [intro, setIntro] = useState(null);
  const [universes, setUniverses] = useState([]);
  const [logos, setLogos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [googleRating, setGoogleRating] = useState(null);

  // Fetch Google Business rating (if configured in admin/content/google_reviews)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/content/google_reviews");
        const v = r.data?.value;
        if (v && v.rating && v.count) setGoogleRating(v);
      } catch {}
    })();
  }, []);

  useSEO({
    title: "",
    description: "Atelier artisanal de gravure et découpe laser sur mesure (Isère). Verre, bois, métal, acrylique. Mariage, cadeaux, entreprises. Livraison France.",
    image: hero?.image,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Passion Verre",
      "description": "Atelier artisanal de gravure et découpe laser sur mesure depuis 2009.",
      "image": `${window.location.origin}/og-image.jpg`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Moidieu-Détourbe",
        "postalCode": "38440",
        "addressRegion": "Isère",
        "addressCountry": "FR",
      },
      "geo": { "@type": "GeoCoordinates", "latitude": 45.4806, "longitude": 5.0461 },
      "url": "https://passionverre.com",
      "areaServed": "FR",
      "priceRange": "€€",
      "foundingDate": "2009",
      ...(googleRating ? {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: googleRating.rating,
          reviewCount: googleRating.count,
          bestRating: 5,
          worstRating: 1,
        },
      } : {}),
    },
  });

  // Transform our own /api/files URLs to include a width hint (backend resizes + caches)
  const heroOptimized = React.useMemo(() => {
    const src = hero?.image;
    if (!src) return null;
    try {
      const u = new URL(src, window.location.origin);
      if (u.pathname.startsWith("/api/files/")) {
        u.searchParams.set("w", "1600");
        return u.pathname + "?" + u.searchParams.toString();
      }
      return src;
    } catch { return src; }
  }, [hero?.image]);

  // Preload hero image to kick off loading as early as possible
  useEffect(() => {
    if (!heroOptimized) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = heroOptimized;
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch {} };
  }, [heroOptimized]);

  useEffect(() => {
    (async () => {
      try {
        const [content, u, l, r] = await Promise.all([
          api.get("/content"),
          api.get("/universes"),
          api.get("/client-logos"),
          api.get("/reviews"),
        ]);
        setHero(content.data.home_hero);
        setIntro(content.data.home_intro);
        setUniverses(u.data);
        setLogos(l.data);
        setReviews(r.data.slice(0, 3));
      } catch (e) { console.error(e); }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className={`relative min-h-[100vh] flex items-center grain-overlay ${!heroOptimized ? "bg-[#171717]" : ""}`}
        style={heroOptimized ? {
          backgroundImage: `url(${heroOptimized})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : undefined}
      >
        {!heroOptimized && (
          <>
            <div className="absolute inset-0 opacity-30" style={{
              background: "radial-gradient(circle at 20% 30%, rgba(154,134,64,0.35) 0%, transparent 60%), radial-gradient(circle at 80% 70%, rgba(154,134,64,0.2) 0%, transparent 55%)"
            }} />
            <div className="absolute inset-0" style={{
              backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 80px, rgba(154,134,64,0.05) 80px, rgba(154,134,64,0.05) 81px)"
            }} />
          </>
        )}
        {heroOptimized && (
          <div className="absolute inset-0 bg-gradient-to-b from-[#171717]/70 via-[#171717]/50 to-[#171717]" />
        )}
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <SectionOverline className="mb-5">Atelier artisanal — Depuis 2009</SectionOverline>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-[1.05] mb-6" data-testid="home-hero-title">
              {hero?.title || "L'art de la gravure, l'élégance du sur-mesure"}
            </h1>
            <p className="text-base sm:text-lg text-[#DDD4B2]/85 max-w-xl mb-10 leading-relaxed">
              {hero?.subtitle || "Atelier artisanal de gravure et découpe laser • Vienne (38)"}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/realisations" className="btn-gold px-8 py-4 text-sm uppercase inline-flex items-center gap-3" data-testid="hero-cta-realisations">
                {hero?.cta || "Découvrir nos créations"} <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-outline px-8 py-4 text-sm uppercase inline-flex items-center gap-3" data-testid="hero-cta-contact">
                Nous contacter
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <Ornament />
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 sm:py-32 bg-[#DDD4B2] text-[#171717]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div {...fadeUp} className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <SectionOverline>Qui sommes-nous</SectionOverline>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#171717] mt-4 leading-tight">
                {intro?.title || "Un atelier, une histoire de famille"}
              </h2>
            </div>
            <div className="md:col-span-7 space-y-5 text-base leading-relaxed text-[#171717]/85">
              <p data-testid="home-intro-text">{intro?.text}</p>
              <div className="pt-4 flex gap-6 text-sm uppercase tracking-[0.18em] text-[#9A8640]">
                <span>• Sur-mesure</span>
                <span>• Artisanal</span>
                <span>• Local</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* UNIVERS */}
      <section className="py-24 sm:py-32 bg-[#171717]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <Ornament className="mb-4" />
            <SectionOverline>Nos univers</SectionOverline>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#DDD4B2] mt-3">Pour chaque instant, une création unique</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {universes.map((u, i) => (
              <motion.div
                key={u.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative overflow-hidden aspect-[3/4] cursor-pointer"
                data-testid={`universe-card-${u.id}`}
              >
                <SmartImage src={u.image_url} alt={u.title} width={600} eager={i < 2} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#171717] via-[#171717]/30 to-transparent transition-all duration-500 group-hover:from-[#171717]/90" />
                <div className="absolute inset-0 border border-[#9A8640]/0 group-hover:border-[#9A8640]/60 transition-all duration-500" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="font-serif text-2xl text-white mb-1">{u.title}</h3>
                  <p className="text-xs text-[#DDD4B2]/80 uppercase tracking-[0.18em]">{u.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      {logos.length > 0 && (
        <section className="py-20 bg-[#0f0f0f] border-y border-[#9A8640]/20">
          <div className="max-w-7xl mx-auto px-6 text-center mb-10">
            <SectionOverline>Ils nous ont fait confiance</SectionOverline>
          </div>
          <div className="marquee-mask">
            <Marquee gradient={false} speed={35} pauseOnHover>
              {logos.map((l) => (
                <div key={l.id} className="mx-8 h-20 w-44 flex items-center justify-center bg-white/95 rounded-md px-4 py-3 opacity-80 hover:opacity-100 transition-opacity" data-testid={`client-logo-${l.id}`}>
                  <img src={l.image_url} alt={l.name} loading="lazy" decoding="async" className="max-h-14 max-w-full object-contain" />
                </div>
              ))}
            </Marquee>
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <section className="py-24 sm:py-32 bg-[#171717]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <SectionOverline>Avis clients</SectionOverline>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#DDD4B2] mt-3">Ce qu'ils en disent</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-8 border border-[#9A8640]/25 hover:border-[#9A8640]/60 transition-colors"
                data-testid={`review-card-${r.id}`}
              >
                <div className="flex gap-1 mb-5 text-[#9A8640]">
                  {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
                </div>
                <p className="text-[#DDD4B2]/90 leading-relaxed mb-6 font-light italic">"{r.text}"</p>
                <p className="text-xs uppercase tracking-[0.18em] text-[#9A8640]">{r.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 bg-[#DDD4B2] text-[#171717] relative">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Ornament className="mb-6" />
          <h2 className="font-serif text-4xl sm:text-6xl mb-6 leading-tight">Un projet ?<br/>Parlons-en.</h2>
          <p className="text-base sm:text-lg mb-10 text-[#171717]/80 max-w-2xl mx-auto">
            Chaque création commence par une conversation. Partagez-nous votre idée, nous vous accompagnons de la maquette à la livraison.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-3 bg-[#171717] text-[#DDD4B2] px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-[#9A8640] hover:text-[#171717] transition-all" data-testid="final-cta">
            Nous contacter <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
